import { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { GoBack, LayoutContext, Tabs } from '@/_shared/components';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { useTabs } from '@/_shared/hooks/useTabs';
import { TabCreditsDepartment, TabDepartment, TabAssessmentDepartment } from '../components';
import { getDepartmentTeamsCall } from '../state';

export const DepartmentsDetailScreen = () => {
  const t = useTranslations('departments.details.tabs');
  const { isFeatureEnabled } = useFeatureFlags();
  const creditsFF = isFeatureEnabled(FeatureFlag.CREDITS);

  const { setLayout } = useContext(LayoutContext);

  const { isDesktop } = useBreakingPoints();

  const { departmentId } = useParams();

  const {
    execute: executeGetDepartmentTeams,
    loading: loadingGetDepartmentTeams,
    success: successGetDepartmentTeams,
    error: errorGetDepartmentTeams,
    response: responseGetDepartmentTeams,
  } = getDepartmentTeamsCall.hook();

  const tabsInitialState = creditsFF
    ? [
        {
          title: t('tab1'),
          index: 0,
          active: true,
          dataTestId: 'departments',
        },
        {
          title: t('tab2'),
          index: 1,
          active: false,
          dataTestId: 'assessment',
        },
        {
          title: t('tab3'),
          index: 2,
          active: false,
          dataTestId: 'credits',
        },
      ]
    : [
        {
          title: t('tab1'),
          index: 0,
          active: true,
          dataTestId: 'departments',
        },
        {
          title: t('tab2'),
          index: 1,
          active: false,
          dataTestId: 'assessment',
        },
      ];

  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  useEffect(() => {
    if (departmentId) {
      executeGetDepartmentTeams({ departmentId });
    }
  }, [departmentId, executeGetDepartmentTeams]);

  useEffect(() => {
    setLayout({ title: 'departments' });
  }, [setLayout, t]);

  return (
    <Box>
      <GoBack />
      <Box>
        <Box overflowX={isDesktop ? 'auto' : 'scroll'}>
          <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
        </Box>
        {tabs.map(tab => {
          return (
            <Box key={tab.title} display='flex' flexDirection='column'>
              {tab.index === 0 && tab.active && (
                <TabDepartment
                  departmentId={departmentId}
                  loading={loadingGetDepartmentTeams}
                  error={errorGetDepartmentTeams}
                  success={successGetDepartmentTeams}
                  responseGetDepartmentTeams={responseGetDepartmentTeams}
                />
              )}
              {tab.index === 1 && tab.active && (
                <TabAssessmentDepartment
                  departmentId={departmentId}
                  departmentName={responseGetDepartmentTeams?.department?.name}
                  totalEmployees={
                    responseGetDepartmentTeams?.department?.stats?.totalEmployees || 0
                  }
                  hasCompletedEmployees={
                    (responseGetDepartmentTeams?.department?.stats?.completedEmployees ?? 0) > 0
                  }
                />
              )}
              {tab.index === 2 && tab.active && (
                <TabCreditsDepartment
                  departmentId={departmentId}
                  departmentName={responseGetDepartmentTeams?.department?.name}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
